import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import {useCallback, useEffect, useState} from "react";
import {
    createCompanyAdmin,
    deleteCompanyAdmin,
    getAllCompanyAdmins,
    getCompanyAdminById,
    updateCompanyAdmin
} from "../../api/companyadmin/companyAdminApiClient";
import {getCompanyById} from "../../api/company/companyApiClient";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog";
import UserFormDialog from "../../components/UserFormDialog";
import {getAllCompanies} from "../../api/company/companyApiClient";
import SoftButton from "components/SoftButton";
import SoftInput from "../../components/SoftInput";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {IconButton} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon} from "@mui/icons-material";
import EnhancedTable from "../../components/EnhancedTable";
import DataTable, {renderBase64ImageCell, renderPill} from "../../components/DataTable";
import {getStatusColor, getStatusDisplayName, getUserRoleColor, getRoleDisplayName} from "../../utils/statusUtils";
import {handleApiError, showDeleteSuccess} from "../../utils/notificationUtils";
import ViewCompanyDialog from "../company/components/ViewCompanyDialog";

function Overview() {
    const [companyAdmins, setCompanyAdmins] = useState([]);
    const [filteredCompanyAdmins, setFilteredCompanyAdmins] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [companyAdminToDelete, setCompanyAdminToDelete] = useState(null);
    const [companyAdminToEdit, setCompanyAdminToEdit] = useState(null);
    const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [companyToView, setCompanyToView] = useState(null);
    const [viewCompanyDialogOpen, setViewCompanyDialogOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const isOnChildRoute = location.pathname.includes('/company-admins/') && location.pathname !== '/company-admins';

    const columns = [
        {
            name: "profile Pic",
            value: "profilePic",
            align: "center",
            width: "10",
            renderCell: (row) => renderBase64ImageCell(row, "profilePic"),
        },
        {name: "first Name", value: "firstName", align: "left", width: "10"},
        {name: "Last Name", value: "lastName", align: "left", width: "10"},
        {name: "email", value: "email", align: "left", width: "20"},
        {name: "phone No", value: "phoneNumber", align: "left", width: "10"},
        {
            name: "Company",
            value: "companyName",
            align: "left",
            width: "15",
            renderCell: (row) => (
                row.companyName && row.companyName !== "N/A" ? (
                    <SoftTypography
                        variant="button"
                        fontWeight="medium"
                        color="primary"
                        sx={{cursor: "pointer", "&:hover": {textDecoration: "underline"}}}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCompanyNameClick(row);
                        }}
                    >
                        {row.companyName}
                    </SoftTypography>
                ) : (
                    <SoftTypography variant="button" fontWeight="regular" color="text">
                        {row.companyName || "N/A"}
                    </SoftTypography>
                )
            ),
        },
        {
            name: "position",
            value: "userRole",
            align: "center",
            width: "10",
            renderCell: (row) => renderPill(getRoleDisplayName(row.userRole), getUserRoleColor(row.userRole)),
        },
        {
            name: "status",
            value: "status",
            align: "center",
            width: "10",
            renderCell: (row) => renderPill(getStatusDisplayName(row.status), getStatusColor(row.status)),
        },
        {
            name: "Actions",
            value: "action",
            align: "center",
            width: "10",
            renderCell: (row) => (
                <>
                    <IconButton color="primary" size="small" title="View Details" onClick={() => handleViewClick(row)}>
                        <VisibilityIcon/>
                    </IconButton>
                    <IconButton color="primary" size="small" title="Edit" onClick={() => handleEditClick(row)}>
                        <EditIcon/>
                    </IconButton>
                    <IconButton color="error" size="small" title="Delete" onClick={() => handleDeleteClick(row)}>
                        <DeleteIcon/>
                    </IconButton>
                </>
            ),
        },
    ];

    const loadCompanyAdmins = useCallback(async () => {
        try {
            setIsLoading(true);
            
            const response = await getAllCompanyAdmins();
            // Transform data to include companyName from company object
            const transformedData = response.data.map(admin => ({
                ...admin,
                companyName: admin.company?.name || admin.companyName || 'N/A'
            }));
            setCompanyAdmins(transformedData);
            setFilteredCompanyAdmins(transformedData);
        } catch (error) {
            console.error('Error loading company admins:', error);
            handleApiError(error, 'Failed to load company admins. Please try again.');
            setCompanyAdmins([]);
            setFilteredCompanyAdmins([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCompanyAdmins().then();
    }, [loadCompanyAdmins]);

    const handleCompanyAdminAdded = async (formData) => {
        try {
            await createCompanyAdmin(formData);
            await loadCompanyAdmins();
        } catch (error) {
            console.error('Error adding company admin:', error);
            handleApiError(error, 'Failed to add company admin');
            throw error;
        }
    };

    const handleCompanyAdminUpdated = async (formData) => {
        try {
            await updateCompanyAdmin(companyAdminToEdit.id, formData);
            await loadCompanyAdmins();
        } catch (error) {
            console.error('Error updating company admin:', error);
            handleApiError(error, 'Failed to update company admin');
            throw error;
        }
    };

    const handleOpenAddDialog = () => {
        setCompanyAdminToEdit(null);
        setIsEdit(false);
        setEditAddDialogOpen(true);
    };

    const handleEditClick = async (row) => {
        try {
            setIsLoading(true);
            // Fetch the latest company admin data by ID to ensure we have all the data
            const response = await getCompanyAdminById(row.id);
            const companyAdminData = response.data;
            if (companyAdminData) {
                setCompanyAdminToEdit(companyAdminData);
                setIsEdit(true);
                setEditAddDialogOpen(true);
            }
        } catch (error) {
            console.error('Error loading company admin for edit:', error);
            handleApiError(error, 'Failed to load company admin data for editing');
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewClick = (companyAdmin) => {
        navigate(`/company-admins/${companyAdmin.id}`);
    };

    const handleDeleteClick = (companyAdmin) => {
        setCompanyAdminToDelete(companyAdmin);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteCompanyAdmin(companyAdminToDelete.id);
            showDeleteSuccess('Company admin deleted successfully');
            await loadCompanyAdmins();
            setDeleteDialogOpen(false);
            setCompanyAdminToDelete(null);
        } catch (error) {
            handleApiError(error, 'Failed to delete company admin');
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setCompanyAdminToDelete(null);
    };

    const handleOnSubmit = async (formData) => {
        try {
            if (isEdit) {
                await handleCompanyAdminUpdated(formData);
            } else {
                await handleCompanyAdminAdded(formData);
            }
            setEditAddDialogOpen(false);
            setCompanyAdminToEdit(null);
        } catch (error) {
            console.error('Error submitting company admin:', error);
            throw error;
        }
    };

    const handleCloseDialog = () => {
        setDeleteDialogOpen(false);
        setEditAddDialogOpen(false);
        setCompanyAdminToEdit(null);
        setCompanyAdminToDelete(null);
        setViewCompanyDialogOpen(false);
        setCompanyToView(null);
    };

    const handleCompanyNameClick = async (companyAdmin) => {
        try {
            console.log('handleCompanyNameClick called with:', companyAdmin);
            // Check if company object exists, otherwise fetch using companyId
            if (companyAdmin.company) {
                console.log('Using existing company object:', companyAdmin.company);
                setCompanyToView(companyAdmin.company);
                setViewCompanyDialogOpen(true);
            } else if (companyAdmin.companyId) {
                console.log('Fetching company with ID:', companyAdmin.companyId);
                setIsLoading(true);
                const response = await getCompanyById(companyAdmin.companyId);
                console.log('Company API response:', response);
                if (response && response.data) {
                    console.log('Setting company to view:', response.data);
                    setCompanyToView(response.data);
                    setViewCompanyDialogOpen(true);
                } else {
                    console.error('No company data in response');
                    handleApiError(new Error('Company data not found'), 'Failed to load company details');
                }
            } else {
                console.warn('No companyId found in companyAdmin:', companyAdmin);
                handleApiError(new Error('Company ID not found'), 'Company information is not available');
            }
        } catch (error) {
            console.error('Error loading company details:', error);
            handleApiError(error, 'Failed to load company details');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterCompanyAdmin = (value) => {
        let filtered = companyAdmins.filter(companyAdmin => {
            const searchValue = value.toLowerCase();
            return (companyAdmin.firstName && companyAdmin.firstName.toLowerCase().includes(searchValue)) ||
                (companyAdmin.lastName && companyAdmin.lastName.toLowerCase().includes(searchValue)) ||
                (companyAdmin.email && companyAdmin.email.toLowerCase().includes(searchValue)) ||
                (companyAdmin.phoneNumber && companyAdmin.phoneNumber.toLowerCase().includes(searchValue));
        });
        setFilteredCompanyAdmins(filtered);
    };

    return (
        <DashboardLayout>
            <SoftBox pt={2} px={2} width="100%">
                <SoftBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
                    <SoftBox display="flex" alignItems="center" gap={2}>
                        {isOnChildRoute && (
                            <IconButton
                                onClick={() => navigate('/company-admins')}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    }
                                }}
                            >
                                <ArrowBackIcon/>
                            </IconButton>
                        )}
                        <SoftTypography variant="h6" fontWeight="medium">
                            {isOnChildRoute ? 'Company Admin Details' : 'Company Admins'}
                        </SoftTypography>
                    </SoftBox>
                    {!isOnChildRoute && (
                        <>
                            <SoftBox flexGrow={1} maxWidth={560}>
                                <SoftInput
                                    placeholder="Type here to filter by name, email or phone"
                                    icon={{component: "search", direction: "left"}}
                                    onChange={(event) => handleFilterCompanyAdmin(event.target.value)}
                                />
                            </SoftBox>
                            <SoftButton color="primary" onClick={handleOpenAddDialog} sx={{flexShrink: 0}}>
                                Add Company Admin
                            </SoftButton>
                        </>
                    )}
                </SoftBox>
            </SoftBox>
            <SoftBox p={2} width="100%">
                {isOnChildRoute ? (
                    <Outlet/>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <EnhancedTable
                                TableComponent={DataTable}
                                isLoading={isLoading}
                                data={filteredCompanyAdmins}
                                loadingMessage="Loading company admins..."
                                noDataMessage="No company admins found"
                                columns={columns}
                            />
                        </Grid>
                    </Grid>
                )}
            </SoftBox>
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onDelete={handleDeleteConfirm}
                warningMessage={`Are you sure you want to delete ${companyAdminToDelete?.firstName || ""} ${companyAdminToDelete?.lastName || ""}? This action cannot be undone.`}
            />
            <UserFormDialog
                open={editAddDialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleOnSubmit}
                user={companyAdminToEdit}
                isEdit={isEdit}
                entityLabel="Company Admin"
                defaultUserRole="COMPANY_ADMIN"
                minAge={18}
                extraFields={[{
                    key: "companyId",
                    label: "Company",
                    type: "asyncSelect",
                    loader: getAllCompanies,
                    getValue: (c) => c.companyId,
                    getLabel: (c) => c.name,
                    required: true,
                    span: 12,
                }]}
            />
            <ViewCompanyDialog
                open={viewCompanyDialogOpen}
                onClose={handleCloseDialog}
                company={companyToView}
            />
        </DashboardLayout>
    );
}

export default Overview;

